<template>
  <dara-single-content-layout v-loading="fullscreenLoading" container-size="lg">
    <template #content>
      <dara-row class="mb--md d-flex justify-content-center justify-content--space-between align-items-end">
        <dara-col :xs="24" :span="20" class="mb--sm mt--xl">
          <dara-header>
            {{ company_name }}
          </dara-header>
          <dara-p class="dara-u-text--medium">
            {{ $t('analytics-description-page') }}
          </dara-p>
        </dara-col>
        <dara-col :xs="24" :span="4" class="mb--md d-flex justify-content-end">
          <div class="cursor--pointer ml--lg gray-squared-button d-flex justify-content-center align-items-center" name="downloadData" @click="downloadData">
            <el-tooltip effect="dark" :content="$t('download-csv')" placement="top">
              <i class="el-icon-download" />
            </el-tooltip>
          </div>
        </dara-col>
      </dara-row>

      <dara-row class="mt--xl mb--xl d-flex justify-content-center justify-content--space-between">
        <dara-col :xs="24" :span="8" class="color--info gray-card">
          <dara-header size="sm">
            {{ $t('articles') }}
          </dara-header>
          <dara-header size="lg" class="mt--sm mb--sm color--primary">
            {{ articlesAmount }}
          </dara-header>
        </dara-col>
        <dara-col :xs="24" :span="8" class="color--info gray-card">
          <dara-header size="sm">
            {{ $t('questions') }}
          </dara-header>
          <dara-header size="lg" class="mt--sm mb--sm color--primary">
            {{ questionsAmount }}
          </dara-header>
        </dara-col>
        <dara-col :xs="24" :span="8" class="color--info gray-card">
          <dara-header size="sm">
            {{ $t('tracks') }}
          </dara-header>
          <dara-header size="lg" class="mt--sm mb--sm color--primary">
            {{ tracksAmount }}
          </dara-header>
        </dara-col>
      </dara-row>

      <dara-row class="mt--xl mb--xl d-flex">
        <dara-col
          v-if="chartData"
          :xs="24"
          :span="16"
          class="color--info answer-card gray-card"
          :class="chartData[1][1] == 0 || chartData[2][1] == 0 ? 'd-flex align-items--center justify-content-center' : ''">
          <div v-if="chartData[1][1] > 0 || chartData[2][1] > 0">
            <dara-header size="sm">
              {{ $t('answered-questions') }}
            </dara-header>
            <g-chart
              type="PieChart"
              :data="chartData"
              :options="chartOptions" />
          </div>
          <dara-header v-else size="sm">
            {{ $t('no-questions-to-process') }}
          </dara-header>
        </dara-col>
        <dara-col
          :xs="24"
          :span="8"
          class="color--info gray-card"
          :class="treeMapData.length == 2 ? 'd-flex align-items--center justify-content-center' : ''"
          :style="treeMapData.length == 2 ? 'border-right: 10px solid transparent;' : ''">
          <div v-if="treeMapData.length > 2">
            <dara-header size="sm" class="mb--lg">
              {{ $t('top-tags') }}
            </dara-header>
            <g-chart
              type="TreeMap"
              :data="treeMapData"
              :options="treemapOptions"
              :settings="treemapSettings" />
          </div>
          <dara-header v-else size="sm">
            {{ $t('no-tags-to-process') }}
          </dara-header>
        </dara-col>
      </dara-row>

      <div class="d-flex justify-content--space-between align-items-center">
        <dara-header size="lg">{{ $t('filter-tables') }}: </dara-header>
        <dara-department-chooser
          ref="departmentChooser"
          v-model="departmentFilter"
          :show-label="false"
          :is-filter="true"
          class="ml--xl mt--xl"
          @on-change="onDepartmentChange($event)" />
        <dara-invisible>
          <dara-p weight="strong" class="ml--lg mr--md mb--0 mt--lg">{{ $t('from') }}</dara-p>
          <dara-input
            v-model="startDate"
            type="date"
            :name="$t('begin-date')"
            rules=""
            class="mt--md"
            @change="fetchTables()" />
        </dara-invisible>
        <dara-invisible>
          <dara-p weight="strong" class="ml--lg mr--md mb--0 mt--lg">{{ $t('to') }}</dara-p>
          <dara-input
            v-model="endDate"
            type="date"
            :name="$t('end-date')"
            rules=""
            class="mt--md"
            @change="fetchTables()" />
        </dara-invisible>
      </div>
      <dara-invisible class="dara-u-text--medium">
        {{ $t('analytics-filter-description') }}
      </dara-invisible>

      <dara-users-table
        :label="$t('users-analytics')"
        :show-range-date="false"
        :data="users"
        :data-pagination="usersPagination"
        @fetch="fetchUsers($event)" />

      <dara-table
        :label="$t('articles-analytics')"
        :show-range-date="false"
        :columns="articlesColumns"
        :data="articles"
        :type="'article'"
        :pagination="articlesPagination"
        @fetch="fetchArticles($event)" />

      <dara-table
        :label="$t('questions-analytics')"
        :show-range-date="false"
        :columns="questionsColumns"
        :data="questions"
        :type="'question'"
        :pagination="questionsPagination"
        @fetch="fetchQuestions($event)" />

      <dara-table
        :label="$t('tracks-analytics')"
        :show-range-date="false"
        :columns="tracksColumns"
        :data="tracks"
        :type="'track'"
        :pagination="tracksPagination"
        @fetch="fetchTracks($event)" />
    </template>
  </dara-single-content-layout>
</template>

<script>
import { GChart } from 'vue-google-charts/legacy';
import { toastError } from '@/services/toastService';
import { translate } from '@/services/translate.service';
import { getMonthRange } from '@/services/timeService';

import { getCurrentCompany, getCompanyAnalytics } from '@/modules/company/company.service';
import { getCurrentStoredUser, fetchRanking } from '@/modules/user/user.service';
import { fetchAnalyticsArticles } from '@/modules/article/article.service';
import { fetchQuestionsAnalytics } from '@/modules/question/question.service';
import { fetchTracksAnalytics } from '@/modules/track/track.service';
import { piechartOptions, treeMapOptions, treeMapSettings } from '../chartsConfig';

import DaraSingleContentLayout from '@/layouts/SingleContentLayout.vue';
import DaraDepartmentChooser from '@/modules/department/components/DepartmentChooser.vue';
import DaraUsersTable from '@/modules/user/components/UserRankingTable.vue';

export default {
  name: 'DaraAnalyticsPage',
  components: {
    GChart,
    DaraSingleContentLayout,
    DaraUsersTable,
    DaraDepartmentChooser,
  },
  data() {
    return {
      articlesAmount: 0,
      questionsAmount: 0,
      tracksAmount: 0,
      answeredQuestions: 0,
      unansweredQuestions: 0,
      topTags: [],

      chartData: null,
      treeMapData: [
        ['Title', 'Parent', 'Used (amount)'],
        ['Tags', null, 0],
      ],
      chartOptions: piechartOptions,
      treemapOptions: treeMapOptions,
      treemapSettings: treeMapSettings,

      departmentFilter: 'all',

      articlesColumns: ['title', 'total_rating', 'ratings', 'readings', 'visits'],
      questionsColumns: ['title', 'total_answers', 'status', 'anonymous', 'created_at'],
      tracksColumns: ['title', 'followers', 'articles', 'visits', 'created_at'],
      users: [],
      articles: [],
      questions: [],
      tracks: [],
      usersBy: 'total_rating',
      articlesBy: 'total_rating',
      questionsBy: 'total_answers',
      tracksBy: 'followers',
      usersOrder: null,
      articlesOrder: null,
      questionsOrder: null,
      tracksOrder: null,
      usersPagination: {
        page: 1,
        limit: 5,
      },
      articlesPagination: {
        page: 1,
        limit: 5,
      },
      questionsPagination: {
        page: 1,
        limit: 5,
      },
      tracksPagination: {
        page: 1,
        limit: 5,
      },

      fullscreenLoading: true,
      company: '',
      company_name: '',

      date: new Date(),
      startDate: '',
      endDate: '',
    };
  },
  created() {
    this.buildRangeDate();
    this.fetchTables();
  },
  mounted() {
    this.fetchCompany();
  },
  methods: {
    fetchCompany() {
      getCurrentCompany(getCurrentStoredUser().company_id)
        .then(data => {
          this.company = data;
          this.company_name = this.company.company_name;
          this.fetchCompanyAnalytics();
        })
        .catch(() => toastError(translate('error-fetching-company-data')));
    },
    fetchCompanyAnalytics() {
      getCompanyAnalytics()
        .then(data => {
          // FIRST DATA SECTION
          this.articlesAmount = data.articlesAmount;
          this.questionsAmount = data.questionsAmount;
          this.tracksAmount = data.tracksAmount;

          // SECOND DATA SECTION
          this.answeredQuestions = data.answeredQuestions;
          this.unansweredQuestions = data.notAnsweredQuestions;
          this.chartData = [
            ['Status', 'Questions'],
            [`${translate('answered-plural')}: ${data.answeredQuestions}`, data.answeredQuestions],
            [`${translate('unanswered-plural')}: ${data.notAnsweredQuestions}`, data.notAnsweredQuestions],
          ];

          data.topTags.map(tag => {
            this.treeMapData.push([`# ${tag.name} (${tag.amount})`, 'Tags', parseInt(tag.amount)]);
            this.topTags.push([`${tag.name}, ${tag.amount}`]);
            return true;
          });

          this.fullscreenLoading = false;
        })
        .catch(() => toastError(translate('error-fetching-company-data')));
    },
    fetchTables() {
      this.fetchUsers(this.usersPagination);
      this.fetchArticles(this.articlesPagination);
      this.fetchQuestions(this.questionsPagination);
      this.fetchTracks(this.tracksPagination);
    },
    fetchUsers(payload = {}) {
      const userPayload = this.basePayload(payload);
      userPayload.by = payload.by == null ? this.usersBy : payload.by;
      userPayload.order = payload.order == null ? this.usersOrder : payload.order;

      if (userPayload.by != null) { this.usersBy = payload.by; }
      if (userPayload.order != null) { this.usersOrder = payload.order; }

      fetchRanking(userPayload)
        .then(data => {
          this.users = data.users;
          this.usersPagination.total = parseInt(data.total_users);
        })
        .catch(() => {
          toastError(`${translate('error-getting-users')}`);
        });
    },
    fetchArticles(payload = {}) {
      const articlePayload = this.basePayload(payload);
      articlePayload.by = payload.by == null ? this.articlesBy : payload.by;
      articlePayload.order = payload.order == null ? this.articlesOrder : payload.order;

      if (articlePayload.by != null) { this.articlesBy = payload.by; }
      if (articlePayload.order != null) { this.articlesOrder = payload.order; }

      fetchAnalyticsArticles(articlePayload)
        .then(data => {
          this.articles = data.articles;
          this.articlesPagination.total = parseInt(data.total_articles);
        })
        .catch(() => toastError(translate('error-fetching-company-data')));
    },
    fetchQuestions(payload = {}) {
      const questionPayload = this.basePayload(payload);
      questionPayload.by = payload.by == null ? this.questionsBy : payload.by;
      questionPayload.order = payload.order == null ? this.questionsOrder : payload.order;

      if (questionPayload.by != null) { this.questionsBy = payload.by; }
      if (questionPayload.order != null) { this.questionsOrder = payload.order; }

      fetchQuestionsAnalytics(questionPayload)
        .then(data => {
          this.questions = data.questions;
          this.questions.map(question => {
            question.created_at = `${question.created_at.slice(8, 10)}/${question.created_at.slice(5, 7)}/${question.created_at.slice(0, 4)}`;
            question.status = question.status === true ? translate('answered') : translate('unanswered');
            question.anonymous = question.anonymous === true ? translate('yes') : translate('no');
            return true;
          });
          this.questionsPagination.total = parseInt(data.total_questions);
        })
        .catch(() => toastError(translate('error-fetching-company-data')));
    },
    fetchTracks(payload = {}) {
      payload.department_id = this.departmentFilter !== 'all' ? this.departmentFilter : null;
      payload.by = payload.by == null ? this.tracksBy : payload.by;
      payload.order = payload.order == null ? this.tracksOrder : payload.order;

      if (payload.by != null) { this.tracksBy = payload.by; }
      if (payload.order != null) { this.tracksOrder = payload.order; }

      fetchTracksAnalytics(payload)
        .then(data => {
          this.tracks = data.tracks;
          this.tracks.map(track => {
            track.created_at = `${track.created_at.slice(8, 10)}/${track.created_at.slice(5, 7)}/${track.created_at.slice(0, 4)}`;
            return true;
          });
          this.tracksPagination.total = parseInt(data.total_tracks);
        })
        .catch(() => toastError(translate('error-fetching-company-data')));
    },
    onDepartmentChange(choosed) {
      this.departmentFilter = choosed.id;
      this.fetchTables();
    },
    buildRangeDate() {
      const { start, end } = getMonthRange();
      this.startDate = start;
      this.endDate = end;
    },
    basePayload(payload = {}) {
      return {
        ...payload,
        department_id: this.departmentFilter !== 'all' ? this.departmentFilter : null,
        startDate: this.startDate,
        endDate: this.endDate,
      };
    },
    async downloadData() {
      this.fullscreenLoading = true;
      if (this.startDate >= this.endDate) {
        toastError(translate('error-start-date-greater-then-end-date'));
        this.fullscreenLoading = false;
        return;
      }

      const aux = {
        ...this.basePayload(),
        page: 1,
        limit: 9999999999,
      };

      let allUsers = null;
      let allArticles = null;
      let allQuestions = null;
      let allTracks = null;

      const userPayload = {
        by: this.usersBy,
        order: this.usersOrder,
        ...aux,
      };
      const articlePayload = {
        by: this.articlesBy,
        order: this.articlesOrder,
        ...aux,
      };
      const questionPayload = {
        by: this.questionsBy,
        order: this.questionsOrder,
        ...aux,
      };
      const trackPayload = {
        by: this.tracksBy,
        order: this.tracksOrder,
        ...aux,
      };

      await fetchRanking(userPayload)
        .then(data => {
          allUsers = data.users;
        })
        .catch(() => {
          toastError(`${translate('error-getting-users')}`);
        });

      await fetchAnalyticsArticles(articlePayload)
        .then(data => {
          allArticles = data.articles;
        })
        .catch(() => toastError(translate('error-fetching-company-data')));

      await fetchQuestionsAnalytics(questionPayload)
        .then(data => {
          allQuestions = data.questions;
          allQuestions.map(question => {
            question.created_at = `${question.created_at.slice(8, 10)}/${question.created_at.slice(5, 7)}/${question.created_at.slice(0, 4)}`;
            question.status = question.status === true ? translate('answered') : translate('unanswered');
            question.anonymous = question.anonymous === true ? translate('yes') : translate('no');
            return true;
          });
        })
        .catch(() => toastError(translate('error-fetching-company-data')));

      await fetchTracksAnalytics(trackPayload)
        .then(data => {
          allTracks = data.tracks;
          allTracks.map(track => {
            track.created_at = `${track.created_at.slice(8, 10)}/${track.created_at.slice(5, 7)}/${track.created_at.slice(0, 4)}`;
            return true;
          });
        })
        .catch(() => toastError(translate('error-fetching-company-data')));

      const users = [[translate('user-name'), translate('total-rating'), translate('created-articles'), translate('readed-articles'), translate('new-followers')]];
      allUsers.map(user =>
        users.push([`${user.name} ${user.last_name}`,
          user.total_rating,
          user.created_articles,
          user.readed_articles,
          user.followers]));

      const articles = [[translate('title'), translate('total_rating'), translate('ratings'), translate('readings'), translate('visits')]];
      allArticles.map(article => {
        article.title = article.title.replace('#', '');
        article.title = article.title.replace(',', '');
        articles.push([article.title,
          article.total_rating,
          article.ratings,
          article.readings,
          article.visits]);
        return true;
      });

      const questions = [[translate('title'), translate('total_answers'), translate('status'), translate('anonymous'), translate('created_at')]];
      allQuestions.map(question => {
        question.title = question.title.replace('#', '');
        question.title = question.title.replace(',', '');
        questions.push([question.title,
          question.total_answers,
          question.status,
          question.anonymous,
          question.created_at]);
        return true;
      });

      const tracks = [[translate('title'), translate('followers'), translate('articles'), translate('visits'), translate('created_at')]];
      allTracks.map(track => {
        track.name = track.name.replace('#', '');
        track.name = track.name.replace(',', '');
        tracks.push([track.name,
          track.followers,
          track.articles,
          track.visits,
          track.created_at]);
        return true;
      });

      // BUILDING CSV
      let csvContent = 'data:text/csv;charset=utf-8,';
      csvContent += ''.concat(`\n${translate('articles-label')}:, ${this.articlesAmount}\n`);
      csvContent += ''.concat(`${translate('questions-label')}:, ${this.questionsAmount}\n`);
      csvContent += ''.concat(`${translate('tracks-label')}:, ${this.tracksAmount}\n`);

      csvContent += ''.concat(`\n${translate('answered-questions-label')}:, ${this.answeredQuestions}\n`);
      csvContent += ''.concat(`${translate('unanswered-questions-label')}:, ${this.unansweredQuestions}\n`);

      csvContent += ''.concat(`\n\n${translate('top-tags-label')}\n`);
      csvContent += ''.concat(`${translate('tag-label')}, ${translate('tag-amount-label')}\n`).concat(this.topTags.map(e => e.join(',')).join('\n'));

      csvContent += ''.concat(`\n\n\n${translate('data-period-label')}: (${this.startDate}) ${translate('to-period')} (${this.endDate})\n`);
      csvContent += ''.concat(`\n${translate('users-table-label')} - ${translate('order')} ${translate('by')} "${translate(this.usersBy)}"\n`)
        .concat(users.map(e => e.join(',')).join('\n'));

      csvContent += ''.concat(`\n\n\n${translate('articles-table-label')} - ${translate('order')} ${translate('by')} "${translate(this.articlesBy)}"\n`)
        .concat(articles.map(e => e.join(',')).join('\n'));

      csvContent += ''.concat(`\n\n\n${translate('questions-table-label')} - ${translate('order')} ${translate('by')} "${translate(this.questionsBy)}"\n`)
        .concat(questions.map(e => e.join(',')).join('\n'));

      csvContent += ''.concat(`\n\n\n${translate('tracks-table-label')} - ${translate('order')} ${translate('by')} "${translate(this.tracksBy)}"\n`)
        .concat(tracks.map(e => e.join(',')).join('\n'));

      const encodedUri = encodeURI(csvContent);
      const donwload = document.createElement('a');
      donwload.setAttribute('href', encodedUri);
      donwload.setAttribute('download', `${this.company_name} (analytics).csv`);
      document.body.appendChild(donwload);
      donwload.click();
      this.fullscreenLoading = false;
    },
  },
};
</script>

<style scoped>
  .answer-card {
    width: 42rem;
  }
  .gray-card {
    border-left: 10px solid transparent;
    border-top: 20px solid transparent;
    border-bottom : 20px solid transparent;
    border-radius: 5px;
    margin: 0rem 0.6rem 0rem 0.6rem;
    background-color: #f4f4f5 !important;
  }
  .gray-squared-button {
    height: 2.6rem;
    width: 2.6rem;
    border-radius: 5px;
    color: rgb(161, 161, 161);
    background-color: #f4f4f5 !important;
  }
  .gray-squared-button:hover {
    background-color: #f0f0f0 !important;
  }
</style>
